import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    AlertTriangle,
    Clock3,
    Lock,
    Loader2,
    RotateCcw,
    ScrollText,
    ShieldCheck,
} from "lucide-react";
import { PartnerAPI } from "@/lib/partnerApi";
import { formatApiError } from "@/context/PartnerAuthContext";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
        });
    } catch {
        return iso;
    }
}

function hoursRemaining(createdAt) {
    if (!createdAt) return 0;
    const created = new Date(createdAt).getTime();
    const now = Date.now();
    const elapsed = (now - created) / 3600_000;
    return Math.max(0, 48 - elapsed);
}

export default function ChangesPanel() {
    const [data, setData] = useState(null);
    const [reverting, setReverting] = useState(null);

    const load = async () => {
        try {
            const d = await PartnerAPI.changes();
            setData(d.changes || []);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    };
    useEffect(() => {
        load();
    }, []);

    if (!data) {
        return (
            <div className="py-10 text-center">
                <Loader2 size={24} className="animate-spin inline text-gold" />
            </div>
        );
    }

    const revert = async (id) => {
        setReverting(id);
        try {
            await PartnerAPI.revert(id);
            toast.success("Change reverted · previous version restored");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setReverting(null);
        }
    };

    return (
        <section data-testid="partner-changes-panel" className="space-y-4">
            <header>
                <span className="divider-rule text-gold">
                    <ScrollText size={12} /> Published Changes
                </span>
                <h2 className="font-serif text-3xl text-cream mt-5">
                    Change Log
                </h2>
                <p className="text-cream/60 text-sm mt-1 max-w-2xl">
                    Every published change opens a 48-hour review window.
                    Click Revert within the window to restore the previous
                    version instantly. After 48 hours the change locks in.
                </p>
            </header>

            {data.length === 0 ? (
                <div
                    className="p-10 bg-cream/5 border border-cream/10 rounded-sm text-center text-cream/55 text-sm"
                    data-testid="partner-changes-empty"
                >
                    No changes published yet.
                </div>
            ) : (
                <ul className="space-y-3">
                    {data.map((c) => {
                        const hours = c.within_window
                            ? hoursRemaining(c.created_at)
                            : 0;
                        const isRevert = c.action === "revert";
                        return (
                            <li
                                key={c.id}
                                data-testid={`partner-change-${c.id}`}
                                className={`p-5 rounded-sm border backdrop-blur flex items-start gap-4 flex-wrap ${
                                    c.locked
                                        ? "bg-cream/5 border-cream/10 opacity-80"
                                        : "bg-cream/5 border-gold/40"
                                }`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-[0.6rem] tracking-[0.28em] uppercase text-gold">
                                            {c.target_type} · {c.target_id}
                                        </span>
                                        {isRevert && (
                                            <span className="text-[0.6rem] tracking-[0.22em] uppercase bg-cream/10 text-cream/80 px-2 py-0.5 rounded-full">
                                                Revert
                                            </span>
                                        )}
                                        {c.reverted && (
                                            <span className="text-[0.6rem] tracking-[0.22em] uppercase bg-cream/15 text-cream/80 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                                <ShieldCheck size={10} /> Reverted
                                            </span>
                                        )}
                                        {c.locked && !c.reverted && (
                                            <span className="text-[0.6rem] tracking-[0.22em] uppercase bg-cream/10 text-cream/60 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                                <Lock size={10} /> Locked
                                            </span>
                                        )}
                                    </div>
                                    <p className="font-serif text-base text-cream mt-1">
                                        {Object.keys(c.after || {}).join(", ") || "—"} updated
                                    </p>
                                    <p className="text-cream/55 text-xs mt-1 inline-flex items-center gap-1.5">
                                        <Clock3 size={10} />{" "}
                                        {fmtDate(c.created_at)}{" "}
                                        {c.can_revert && (
                                            <span className="text-gold">
                                                · {hours.toFixed(1)}h remain
                                            </span>
                                        )}
                                    </p>
                                </div>
                                {c.can_revert && (
                                    <button
                                        type="button"
                                        onClick={() => revert(c.id)}
                                        disabled={reverting === c.id}
                                        data-testid={`partner-revert-${c.id}`}
                                        className="btn-pill btn-gold text-xs"
                                    >
                                        {reverting === c.id ? (
                                            <Loader2 size={12} className="animate-spin" />
                                        ) : (
                                            <>
                                                <RotateCcw size={12} /> Revert
                                            </>
                                        )}
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            <div className="p-4 bg-cream/5 border border-cream/10 rounded-sm text-cream/60 text-xs leading-relaxed inline-flex items-start gap-2">
                <AlertTriangle size={14} className="text-gold shrink-0 mt-0.5" />
                After 48 hours a change locks in. Director K can override a
                locked change via the admin console.
            </div>
        </section>
    );
}
